/**
 * ClubVideosSection
 * Component voor het beheren en weergeven van club video's
 */

import React, { useState, useEffect } from 'react';
import { getAllVideos, VideoData } from '../../../services/videoService';
import SuperKonnectedCarousel from '../../SuperKonnectedCarousel';
import { Icon } from '../../icons';
import { Video, Upload, Loader, X } from '../../icons';

interface ClubVideosSectionProps {
  clubId: string;
  entityId: string;
  clubName: string;
  canUpload: boolean; // owner, organizer, board
}

const ClubVideosSection: React.FC<ClubVideosSectionProps> = ({
  clubId,
  entityId,
  clubName,
  canUpload
}) => {
  const [clubVideos, setClubVideos] = useState<VideoData[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);

  useEffect(() => {
    const loadClubVideos = async () => {
      try {
        setLoading(true);
        const allVideos = await getAllVideos();
        // Filter videos uploaded by this entity
        const filtered = allVideos.filter(
          video => video.entityId === entityId && video.uploadedByEntity === true
        );
        setClubVideos(filtered);
      } catch (error) {
        console.error('Error loading club videos:', error);
      } finally {
        setLoading(false);
      }
    };

    loadClubVideos();
  }, [entityId]);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-semibold text-gray-900 flex items-center space-x-2">
            <Icon icon={Video} size={20} strokeWidth={1.5} />
            <span>Club Video's</span>
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Video's geüpload door {clubName}
          </p>
        </div>
        {canUpload && (
          <button
            onClick={() => setShowUploadModal(true)}
            className="px-4 py-2 bg-gradient-to-r from-orange-500 to-yellow-600 text-white rounded-lg font-medium hover:shadow-lg transition-all flex items-center space-x-2"
          >
            <Icon icon={Upload} size={16} strokeWidth={1.5} />
            <span>Video Uploaden</span>
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Icon icon={Loader} size={24} className="animate-spin text-orange-500" />
          <span className="ml-2 text-gray-600">Video's laden...</span>
        </div>
      ) : clubVideos.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Icon icon={Video} size={48} className="mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500 mb-2">Nog geen video's geüpload</p>
          {canUpload && (
            <button
              onClick={() => setShowUploadModal(true)}
              className="text-orange-600 hover:text-orange-700 font-medium"
            >
              Upload je eerste video →
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            {clubVideos.length} video{clubVideos.length !== 1 ? "'s" : ""} gevonden
          </p>
          {/* Mini carousel preview - toon eerste 3 video's */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {clubVideos.slice(0, 3).map((video) => (
              <div
                key={video.id}
                className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:border-orange-300 transition-colors"
              >
                <div className="aspect-video bg-gradient-to-br from-orange-100 to-yellow-100 rounded-lg mb-2 flex items-center justify-center">
                  {video.thumbnailUrl ? (
                    <img
                      src={video.thumbnailUrl}
                      alt={video.title}
                      className="w-full h-full object-cover rounded-lg"
                    />
                  ) : (
                    <Icon icon={Video} size={32} className="text-gray-400" />
                  )}
                </div>
                <h4 className="font-medium text-gray-900 text-sm line-clamp-2">
                  {video.title}
                </h4>
                <p className="text-xs text-gray-500 mt-1">
                  {video.views} weergaven
                </p>
              </div>
            ))}
          </div>
          {clubVideos.length > 3 && (
            <button
              onClick={() => setShowUploadModal(true)}
              className="w-full py-2 text-sm text-orange-600 hover:text-orange-700 font-medium border border-orange-200 rounded-lg hover:bg-orange-50 transition-colors"
            >
              Bekijk alle {clubVideos.length} video's →
            </button>
          )}
        </div>
      )}

      {/* Upload Modal - SuperKonnectedCarousel met entity context */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Video Uploaden voor {clubName}
              </h3>
              <button
                onClick={() => setShowUploadModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <Icon icon={X} size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              <SuperKonnectedCarousel isAdmin={canUpload} filterMode="all" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClubVideosSection;
